import { Component, DestroyRef, computed, inject, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@services/toast.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '@services/auth.service';
import { JwtService } from '@services/jwt.service';
import { passwordMatchValidator, strongPasswordValidator } from '@/app/utils';
import { ChangePasswordVerificationDialog } from '../change-password-verification-dialog/change-password-verification-dialog';

@Component({
  selector: 'app-change-password',
  imports: [ReactiveFormsModule],
  templateUrl: './change-password.html',
  styleUrl: './change-password.scss'
})
export class ChangePassword {
  readonly closed = output<void>();
  readonly otpOpenChange = output<boolean>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly jwtService = inject(JwtService);
  private readonly toast = inject(ToastService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly dialog = inject(MatDialog);
  private otpDialogRef: MatDialogRef<ChangePasswordVerificationDialog> | null = null;

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly showOldPassword = signal(false);
  protected readonly showNewPassword = signal(false);
  protected readonly showConfirmPassword = signal(false);

  protected readonly inputTypeOldPassword = computed(() => (this.showOldPassword() ? 'text' : 'password'));
  protected readonly inputTypeNewPassword = computed(() => (this.showNewPassword() ? 'text' : 'password'));
  protected readonly inputTypeConfirmPassword = computed(() => (this.showConfirmPassword() ? 'text' : 'password'));

  protected readonly form = this.formBuilder.group(
    {
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, strongPasswordValidator]],
      confirmPassword: ['', [Validators.required]]
    },
    { validators: passwordMatchValidator }
  );

  get controls() {
    return this.form.controls;
  }

  toggleOldPasswordVisibility(): void {
    this.showOldPassword.update((value) => !value);
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword.update((value) => !value);
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.update((value) => !value);
  }

  get oldPasswordValue(): string {
    return this.form.getRawValue().oldPassword ?? '';
  }

  get newPasswordValue(): string {
    return this.form.getRawValue().newPassword ?? '';
  }

  get confirmPasswordValue(): string {
    return this.form.getRawValue().confirmPassword ?? '';
  }

  submit(): void {
    this.submitted.set(true);
    this.errorMessage.set(null);

    if (this.form.invalid) {
      return;
    }

    this.loading.set(true);
    this.spinner.show();
    const payload = {
      OldPassword: this.oldPasswordValue.trim(),
      CreatePassword: this.newPasswordValue.trim(),
      ConfirmPassword: this.confirmPasswordValue.trim()
    };

    this.authService.requestChangePassword(payload).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.spinner.hide();
        if (response.success && response.data) {
          this.toast.success(response.message || 'Verification code sent to your email.');
          this.openOtpDialog(response.data.UniqueId || '');
        } else {
          const msg = response.message || 'Unable to process change password request.';
          this.toast.error(msg);
          this.errorMessage.set(msg);
        }
      },
      error: (error) => {
        this.loading.set(false);
        this.spinner.hide();
        const msg = error?.error?.message ?? 'Unable to update your password. Please try again.';
        this.toast.error(msg);
        this.errorMessage.set(msg);
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  private openOtpDialog(uniqueId: string): void {
    this.otpDialogRef?.close();

    const ref = this.dialog.open(ChangePasswordVerificationDialog, {
      autoFocus: false,
      disableClose: true,
      width: '550px',
      maxWidth: '95vw',
      panelClass: 'app-confirm-dialog-panel'
    });

    const otp = ref.componentRef;
    otp?.setInput('uniqueId', uniqueId);
    otp?.setInput('userId', this.jwtService.getUserId() || '');
    otp?.setInput('emailId', this.jwtService.getEmail() || '');
    otp?.setInput('oldPassword', this.oldPasswordValue);
    otp?.setInput('newPassword', this.newPasswordValue);
    otp?.setInput('confirmPassword', this.confirmPasswordValue);

    ref.componentInstance.confirmed.subscribe(() => {
      ref.close();
      this.closed.emit();
      this.authService.logout();
    });
    ref.componentInstance.closed.subscribe(() => {
      ref.close();
      this.closed.emit();
    });

    // Panel destroyed (logout / navigation) while the dialog is open — don't leave it floating.
    const unregisterOnDestroy = this.destroyRef.onDestroy(() => ref.close());

    ref.afterClosed().subscribe(() => {
      unregisterOnDestroy();
      if (this.otpDialogRef === ref) {
        this.otpDialogRef = null;
        this.otpOpenChange.emit(false);
      }
    });
    this.otpDialogRef = ref;
    this.otpOpenChange.emit(true);
  }
}
